import { CopyIcon, TrashIcon } from '@radix-ui/react-icons';
import { Badge, IconButton } from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonTableHoC } from 'components/common/table';
import { SessionHitsSummariesToolbar } from 'components/sections/analytics/session-hits-summaries/toolbar';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { HittersContext } from 'contexts/hitters.context';
import { MachineContext } from 'contexts/machine.context';
import { lightFormat } from 'date-fns';
import format from 'date-fns-tz/format';
import parseISO from 'date-fns/parseISO';
import { SectionName } from 'enums/route.enums';
import { TABLES } from 'enums/tables';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IMenuAction } from 'interfaces/i-menus';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { SessionHelper } from 'lib_ts/classes/session.helper';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_DATE_FORMAT,
  LOCAL_PRETTY_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ISHDeleteRequest } from 'lib_ts/interfaces/session-hits/i-delete-request';
import { ISHSummary } from 'lib_ts/interfaces/session-hits/i-summary';
import { first, isNil } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import { HittersService } from 'services/hitters.service';

const PAGE_SIZES = TABLES.PAGE_SIZES.MD;
const SHOW_TYPE_COLUMN = false;

export const SessionHitsSummariesTable = (props: {
  // has implications for showing hitter name and where clicking rows will move to
  within: SectionName.HitterLibrary | SectionName.Analytics;
  // e.g. after reassigning sessions, trigger reload
  refetchSessions: () => Promise<void>;
  vFlex?: boolean;
}) => {
  // for re-rendering the whole table HOC after deleting (e.g. to reset checkboxes)
  const [key, setKey] = useState(Date.now());

  const navigate = useNavigate();
  const { machine } = useContext(MachineContext);
  const { hitterOptions } = useContext(HittersContext);

  const loading = useAnalyticsStore((s) => s.loadingSessions || s.loadingHits);
  const sessions = useAnalyticsStore((s) => s.sessions);
  const sessionsFilter = useAnalyticsStore((s) => s.sessionsFilter);

  const [updating, setUpdating] = useState(false);

  const filteredSessions = useMemo(() => {
    return sessions
      .filter(
        (session) =>
          isNil(sessionsFilter.isInGame) ||
          !sessionsFilter.isInGame === !session.isInGame
      )
      .filter(
        (session) =>
          sessionsFilter.hitterIDs.length === 0 ||
          sessionsFilter.hitterIDs.includes(session.hitterID ?? '')
      )
      .filter(
        (session) =>
          sessionsFilter.startDates.length === 0 ||
          sessionsFilter.startDates.includes(
            lightFormat(parseISO(session.startDate), LOCAL_DATE_FORMAT)
          )
      );
  }, [sessions, sessionsFilter]);

  const [dialogDelete, setDialogDelete] = useState<{
    key: number;
    models: ISHSummary[];
  }>();

  const [reassignHitterID, setReassignHitterID] = useState<string>();

  // select new hitter to reassign hits
  const [dialogReassign, setDialogReassign] = useState<{
    key: number;
    summaries: ISHSummary[];
  }>();

  const { isMetric } = machine;

  const columns = useMemo<ITypedTableColumn<ISHSummary>[]>(() => {
    const speedToMetricFn = isMetric ? MetricHelper.mphToKph : undefined;
    const distanceToMetricFn = isMetric ? MetricHelper.ftToM : undefined;

    return [
      {
        label: 'common.hitter-name',
        key: 'hitterName',
        invisible: props.within === SectionName.HitterLibrary,
        formatFn: ({ hitterName }) => hitterName ?? '-',
      },

      {
        label: 'common.start-date',
        key: 'startDate',
        formatFn: ({ startDate }) =>
          format(parseISO(startDate), LOCAL_PRETTY_DATETIME_FORMAT, {
            timeZone: LOCAL_TIMEZONE,
          }),
      },
      {
        label: 'common.type',
        key: 'type',
        invisible: !SHOW_TYPE_COLUMN,
        formatFn: ({ isInGame }) => (
          <Badge color={RADIX.COLOR.NEUTRAL}>
            {isInGame ? 'In-Game' : 'Practice'}
          </Badge>
        ),
      },
      {
        label: 'common.shots',
        key: 'shots',
        align: 'right',
        formatFn: ({ shots }) =>
          SessionHelper.formatHitStat(shots, {
            fixedTo: 0,
          }),
      },
      {
        label: 'common.average-ev',
        subLabel: MetricHelper.getSpeedUnit(isMetric),
        key: 'avgExitMPH',
        align: 'right',
        formatFn: ({ avgExitMPH }) =>
          SessionHelper.formatHitStat(avgExitMPH, {
            fixedTo: 1,
            toMetricFn: speedToMetricFn,
          }),
      },
      {
        label: 'common.average-la',
        subLabel: 'deg',
        key: 'avgVLaunchDeg',
        align: 'right',
        formatFn: ({ avgVLaunchDeg }) =>
          SessionHelper.formatHitStat(avgVLaunchDeg, {
            fixedTo: 1,
          }),
      },
      {
        label: 'common.average-dist',
        subLabel: MetricHelper.getDistanceUnit(isMetric),
        key: 'avgDistanceFT',
        align: 'right',
        formatFn: ({ avgDistanceFT }) =>
          SessionHelper.formatHitStat(avgDistanceFT, {
            fixedTo: 1,
            toMetricFn: distanceToMetricFn,
          }),
      },
      {
        label: '',
        key: '_delete',
        formatFn: (m) => (
          <IconButton
            size={RADIX.BUTTON.SIZE.XS}
            className="btn-floating"
            variant="ghost"
            color={RADIX.COLOR.DANGER}
            onClick={(e) => {
              // prevent triggering the onClick of the parent row (which opens the report)
              e.stopPropagation();

              // open alert to confirm the deletion
              setDialogDelete({
                key: Date.now(),
                models: [m],
              });
            }}
          >
            <TrashIcon />
          </IconButton>
        ),
      },
    ];
  }, [props.within, setDialogDelete, isMetric]);

  const pagination: ITablePageable = {
    paginationID: PaginationID.HittingReports,
    total: filteredSessions.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const select: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: (m: ISHSummary | undefined) => {
      if (!m) {
        return;
      }

      const hitterID = m.hitterID ?? '';

      if (props.within === SectionName.Analytics) {
        navigate(paths.analytics.hitterSession(m.session, hitterID));
        return;
      }

      navigate(paths.hitterLibrary.session(m.session, hitterID));
    },
  };

  const checkedActions = useCallback((checked: ISHSummary[]): IMenuAction[] => {
    const actions: IMenuAction[] = [
      {
        prefixIcon: <CopyIcon />,
        label: 'Reassign Selected',
        onClick: () =>
          setDialogReassign({
            key: Date.now(),
            summaries: checked,
          }),
      },
      {
        prefixIcon: <TrashIcon />,
        label: 'common.delete-selected',
        color: RADIX.COLOR.DANGER,
        onClick: () => {
          setDialogDelete({
            key: Date.now(),
            models: checked,
          });
        },
      },
    ];

    return actions;
  }, []);

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    checkedActions: checkedActions,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'startDate',
      dir: 1,
    },
  };

  return (
    <ErrorBoundary identifier="SessionHitsSummariesTable">
      <CommonTableHoC
        key={key}
        dataTestID="SessionHitsSummaries"
        identifier="SessionHitsSummariesTable"
        displayColumns={columns}
        displayData={filteredSessions}
        loading={loading || updating}
        toolbarContent={
          <SessionHitsSummariesToolbar
            hideHitter={props.within === SectionName.HitterLibrary}
          />
        }
        {...pagination}
        {...checkable}
        {...select}
        {...sort}
        vFlex={props.vFlex}
      />

      {dialogDelete && (
        <CommonConfirmationDialog
          key={dialogDelete.key}
          identifier="ConfirmDeleteHitterSession"
          title="Confirm Deletion"
          content={
            <>
              <p>
                Are you sure you want to delete{' '}
                {dialogDelete.models.length > 1
                  ? 'the selected sessions'
                  : 'this session'}
                ? This action cannot be undone.
              </p>
              <ul>
                {dialogDelete.models.map((m, i) => (
                  <li key={i}>
                    {format(parseISO(m.startDate), LOCAL_DATETIME_FORMAT, {
                      timeZone: LOCAL_TIMEZONE,
                    })}
                    : {m.hitterName}
                  </li>
                ))}
              </ul>
            </>
          }
          action={{
            label: 'common.delete',
            color: RADIX.COLOR.DANGER,
            onClick: async () => {
              setUpdating(true);
              await useAnalyticsStore.getState().deleteSessions(
                dialogDelete.models.map((m) => {
                  const o: ISHDeleteRequest = {
                    machineID: m.machineID,
                    session: m.session,
                    hitterID: m.hitterID,
                  };

                  return o;
                })
              );

              await props.refetchSessions();

              // to ensure any checked options get reset since they won't exist anymore
              setKey(Date.now());

              setUpdating(false);
            },
          }}
        />
      )}

      {dialogReassign && (
        <CommonConfirmationDialog
          key={dialogReassign.key}
          identifier="ConfirmReassignHits"
          title="Reassign Hits"
          content={
            <CommonFormGrid columns={1}>
              <p>
                Please select a new hitter below to reassign{' '}
                {dialogReassign.summaries.length} session(s) to.
              </p>

              <CommonSearchInput
                id="reassign-hitter"
                placeholder="common.hitter"
                options={hitterOptions}
                values={reassignHitterID ? [reassignHitterID] : []}
                onChange={(v) => setReassignHitterID(first(v))}
              />
            </CommonFormGrid>
          }
          action={{
            label: 'common.proceed',
            color: RADIX.COLOR.WARNING,
            onClick: async () => {
              if (!reassignHitterID) {
                return;
              }

              setUpdating(true);
              for (const summary of dialogReassign.summaries) {
                await HittersService.getInstance().updateHits({
                  filter: {
                    session: summary.session,
                    hitterID: summary.hitterID || null,
                    hitIDs: [],
                  },
                  model: {
                    hitterID: reassignHitterID,
                  },
                });
              }

              await props.refetchSessions();
              setUpdating(false);
            },
          }}
        />
      )}
    </ErrorBoundary>
  );
};
